import { valibotResolver } from '@hookform/resolvers/valibot'
import { useRouter } from '@tanstack/react-router'
import { Form, Input } from 'antd'
import { useAtom } from 'jotai'
import { useCallback, useEffect, type FC, type FormEvent } from 'react'
import { Controller } from 'react-hook-form'
import toast from 'react-hot-toast'
import { FaCheckCircle } from 'react-icons/fa'

import {
  BasicTable,
  BasicTbody,
  BasicTd,
  BasicTh,
  BasicTr,
  Button,
  ModalBody,
  ModalFooter,
  RequiredTag
} from '@/components/elements'
import {
  ACTIONS,
  ERROR_MESSAGES,
  LABELS,
  SUCCESS_MESSAGES
} from '@/constants/common/message.constants'
import { toastApiErrorMessage } from '@/features/shared/utils'
import { type CreateCustomerDto, type CustomerEntity } from '@/libs/api'
import { useOnChangeForm } from '@/libs/hooks'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'
import { initialCustomerFormValue } from '@/routes/_protected/$tenantKey/settings/customers/$customerId/-models'
import { CreateCustomerDtoSchema } from '@/routes/_protected/$tenantKey/settings/customers/-models'

import { createCustomer } from '../../api'

type Props = {
  tenantKey: string
  onSuccess: (customer: CustomerEntity) => Promise<void>
  onCancel: () => void
}

export const CustomerCreateModalContent: FC<Props> = ({
  tenantKey,
  onSuccess,
  onCancel
}) => {
  const {
    control,
    setFocus,
    handleSubmit,
    formState: { isSubmitting, isValid, errors }
  } = useOnChangeForm<CreateCustomerDto>({
    defaultValues: {
      ...initialCustomerFormValue
    },
    resolver: valibotResolver(CreateCustomerDtoSchema)
  })

  const router = useRouter()
  const [selectedDcId] = useAtom(selectedDcIdAtom)

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      // formが入れ子になっている場合の対応
      // - submitイベントの親要素への伝播を止める
      if (event) {
        if (typeof event.preventDefault === 'function') {
          event.preventDefault()
        }
        if (typeof event.stopPropagation === 'function') {
          event.stopPropagation()
        }
      }
      return handleSubmit(async (formValue) => {
        try {
          const { data } = await createCustomer({
            tenantKey,
            dto: {
              ...formValue,
              dcId: selectedDcId ?? undefined
            }
          })
          onSuccess(data)
          toast.success(SUCCESS_MESSAGES.REGISTERED_CUSTOMER)
        } catch (error) {
          toastApiErrorMessage(
            error,
            ERROR_MESSAGES.FAILED_TO_REGISTER_CUSTOMER
          )
        }
      })(event)
    },
    [tenantKey, onSuccess, handleSubmit, selectedDcId]
  )

  useEffect(() => {
    const unsubscribe = router.subscribe('onResolved', () => {
      onCancel()
    })
    return () => {
      unsubscribe()
    }
  }, [router, onCancel])

  useEffect(() => {
    setFocus('code')
  }, [setFocus])

  return (
    <form onSubmit={onSubmit}>
      <ModalBody>
        <BasicTable className="w-full table-fixed">
          <colgroup>
            <col className="w-[150px]" />
            <col />
          </colgroup>
          <BasicTbody>
            <BasicTr>
              <BasicTh className="align-top">
                <label htmlFor="code">
                  {LABELS.CUSTOMER_CODE}
                  <RequiredTag className="ml-2" />
                </label>
              </BasicTh>
              <BasicTd colSpan={3} className="align-top">
                <Controller
                  name={'code'}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Input
                        {...field}
                        placeholder={LABELS.CUSTOMER_CODE}
                        className="w-full"
                        id={field.name}
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
            </BasicTr>
            <BasicTr>
              <BasicTh className="align-top">
                <label htmlFor="name">
                  {LABELS.CUSTOMER_NAME}
                  <RequiredTag className="ml-2" />
                </label>
              </BasicTh>
              <BasicTd colSpan={3} className="align-top">
                <Controller
                  name={'name'}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Input
                        {...field}
                        placeholder={LABELS.CUSTOMER_NAME}
                        className="w-full"
                        id={field.name}
                      />
                    </Form.Item>
                  )}
                />
              </BasicTd>
            </BasicTr>
          </BasicTbody>
        </BasicTable>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onCancel}>{ACTIONS.CANCEL}</Button>
        <Button
          type="primary"
          icon={<FaCheckCircle />}
          htmlType="submit"
          disabled={!isValid || Object.keys(errors).length > 0}
          loading={isSubmitting}
        >
          {ACTIONS.REGISTER}
        </Button>
      </ModalFooter>
    </form>
  )
}
